/**
 * ATLETAHUB — GLOBAL ERROR BOUNDARY (HEO-IMPLEMENT-3B)
 * 
 * Captura exceções de renderização no ciclo de vida do React,
 * impedindo tela branca completa e exibindo fallback visual seguro.
 */

import * as React from "react";
import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";
import { sanitizeErrorMessage } from "../../services/health/ErrorNormalizationService";
import { LoggerService } from "../../services/logging/LoggerService";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorReferenceId: string;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
    errorReferenceId: "",
  };

  public static getDerivedStateFromError(error: Error): State {
    const randomHex = Math.random().toString(36).substring(2, 8).toUpperCase();
    return {
      hasError: true,
      error,
      errorReferenceId: `ERR-${Date.now().toString(36).toUpperCase()}-${randomHex}`,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const sanitizedMsg = sanitizeErrorMessage(error?.message || "Erro inesperado na renderização");
    
    // Registro estruturado e sanitizado no Logger central
    LoggerService.error("GlobalErrorBoundary", `[ReactRenderError] ${sanitizedMsg}`, {
      referenceId: this.state.errorReferenceId,
      componentStack: errorInfo.componentStack ? errorInfo.componentStack.substring(0, 300) : undefined,
    });
  }

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorReferenceId: "",
    });
  };

  private handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback && this.state.error) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      return (
        <div
          id="global-error-boundary-container"
          className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6 bg-slate-50 dark:bg-zinc-950 text-slate-900 dark:text-white font-sans transition-colors"
        >
          <div className="max-w-md w-full bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-xl text-center space-y-5">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto ring-8 ring-amber-500/5">
              <AlertTriangle size={32} />
            </div>

            <div className="space-y-2">
              <span className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400">
                Instabilidade Temporária
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                Ops! Algo não carregou como esperado.
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 leading-relaxed">
                Identificamos uma inconsistência visual e isolamos a tela para proteger seus dados.
                Você pode tentar restaurar esta visualização ou recarregar o sistema.
              </p>
            </div>

            {this.state.errorReferenceId && (
              <div className="bg-slate-50 dark:bg-zinc-950/80 rounded-xl p-2.5 border border-slate-200 dark:border-zinc-800/80">
                <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-mono">
                  Código de Referência: <strong className="text-slate-700 dark:text-zinc-300">{this.state.errorReferenceId}</strong>
                </span>
              </div>
            )}

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                id="error-boundary-retry-btn"
                type="button"
                onClick={this.handleReset}
                className="w-full sm:w-auto flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-200 text-xs font-bold transition shadow-sm cursor-pointer min-h-[44px]"
              >
                <RotateCcw size={15} />
                <span>Tentar Novamente</span>
              </button>

              <button
                id="error-boundary-reload-btn"
                type="button"
                onClick={this.handleReload}
                className="w-full sm:w-auto flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition shadow-md cursor-pointer min-h-[44px]"
              >
                <RefreshCw size={15} />
                <span>Recarregar Página</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
